<template>
  <div>
    <div class="filters-section mb-4">
      <b-card>
        <b-card-header class="d-flex justify-content-between align-items-center">
          <h5 class="mb-0">
            <i class="fas fa-filter mr-2"></i>Filters
          </h5>
          <div class="mt-3">
            <b-button @click="exportReport('csv')" variant="outline-primary" class="mr-2">
              <i class="bi bi-download"></i> Export CSV
            </b-button>
            <b-button @click="exportReport('pdf')" variant="outline-secondary">
              <i class="bi bi-file-pdf"></i> Export PDF
            </b-button>
          </div>
        </b-card-header>
        <b-card-body>
          <b-row>
            <b-col md="2">
              <b-form-group label="Hotel:" label-for="hotel">
                <b-form-select id="hotel" v-model="filters.hotelId" :options="hotelOptions"
                  @change="fetchDailySummary" />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Date:" label-for="checkoutDate">
                <b-form-datepicker :date-format-options="dateFormat" id="checkoutDate" v-model="filters.date"
                  @input="fetchDailySummary" class="date-picker" />
              </b-form-group>
            </b-col>
            <b-col md="3">
              <b-form-group label="Payment Status:" label-for="paymentStatus">
                <b-form-select id="paymentStatus" v-model="filters.paymentStatus" :options="paymentStatusOptions"
                  @change="fetchDailySummary"></b-form-select>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </div>
    <div class="summary-cards mb-4">
      <b-row>
        <b-col md="2">
          <b-card class="summary-card">
            <div class="summary-item">
              <h4>Total No. of Checkouts</h4>
              <p class="amount">{{ dailySummary?.totalOuts }}</p>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
    <div class="section">
      <h4 class="section-title mt-4"><i class="bi bi-box-arrow-left"></i> Check-outs</h4>
      <div v-if="!dailySummary?.checkouts || dailySummary?.checkouts.length === 0" class="text-center p-4">
        <p class="mb-0">No data found for the selected criteria.</p>
      </div>
      <b-table 
        v-else
        :items="dailySummary?.checkouts"
        :fields="checkOutFields"
        striped hover small responsive
        :per-page="perPage"
        :current-page="currentPage"
      >
        <template #cell(payment_status)="data">
          <span :class="getPaymentStatusBadgeClass(data.item.payment_status)">
            {{ getPaymentStatusLabel(data.item.payment_status) }}
          </span>
        </template>
        <template #cell(reservation_status)="data">
          <span :class="getReservationStatusBadgeClass(data.item.reservation_status)">
            {{ getReservationStatusLabel(data.item.reservation_status) }}
          </span>
        </template>
      </b-table>
      <b-pagination
        v-if="dailySummary?.checkouts.length"
        v-model="currentPage"
        :total-rows="dailySummary?.checkouts.length"
        :per-page="perPage"
        class="mt-3"
      ></b-pagination>
    </div>
  </div>
</template>

<script>
import { request } from '@/Util/Request';

export default {
  name: 'ReportsIndex',
  data() {
    return {
      filters: {
        date: new Date().toISOString().slice(0, 10),
        hotelId: null,
        paymentStatus: null
      },
      revenue: [],
      checkOutFields: [
        { key: 'id', label: 'Booking ID', sortable: true },
        { key: 'hotel.name', label: 'Hotel', sortable: true },
        { key: 'check_out', label: 'Check-out'},
        { key: 'num_of_adults', label: 'Adults'},
        { key: 'num_of_child', label: 'Children'},
        { key: 'total_price', label: 'Total Price'},
        { key: 'payment_status', label: 'Payment Status', sortable: true },
        { key: 'reservation_status', label: 'Reservation Status', sortable: true }
      ],
      reportTitle: 'Booking & Revenue Reports',
      dateFormat: {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      },
      perPage: 10,
      currentPage: 1,
    }
  },
  props: {
    dailySummary: {
      type: Object,
      required: true
    },
    hotelOptions: {
      type: Array,
      required: true
    },
    paymentStatusOptions: {
      type: Array,
      required: true
    }
  },
  methods: {
    getPaymentStatusBadgeClass(status) {
      switch (status) {
        case 'paid': return 'badge bg-success text-white';
        case 'pending': return 'badge bg-warning text-dark';
        case 'failed': return 'badge bg-danger text-white';
        default: return 'badge bg-secondary text-white';
      }
    },
    getPaymentStatusLabel(status) {
      switch (status) {
        case 'paid': return 'Paid';
        case 'pending': return 'Pending';
        case 'failed': return 'Failed';
        default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
      }
    },
    getReservationStatusBadgeClass(status) {
      switch (status) {
        case 'booked': return 'badge bg-info text-white';
        case 'checked_in': return 'badge bg-primary text-white';
        case 'completed': return 'badge bg-success text-white';
        case 'cancelled': return 'badge bg-danger text-white';
        default: return 'badge bg-secondary text-white';
      }
    },
    getReservationStatusLabel(status) {
      switch (status) {
        case 'booked': return 'Booked';
        case 'checked_in': return 'Checked In';
        case 'completed': return 'Completed';
        case 'cancelled': return 'Cancelled';
        default: return status ? status.charAt(0).toUpperCase() + status.slice(1) : '';
      }
    },
    fetchDailySummary() {
      const params = {
        date: this.filters.date,
        payment_status: this.filters.paymentStatus,
        hotel_id: this.filters.hotelId,
      };
      this.$emit('get-daily-summary', params);
    },
    async exportReport(format) {
      try {
        const params = { ...this.filters, format: format };

        const response = await request({
          url: `/reports/checkout/export`,
          method: 'GET',
          params: params,
          responseType: 'blob',
        });

        const mimeType = format === 'csv'
          ? 'text/csv'
          : 'application/pdf';

        const blob = new Blob([response], { type: mimeType });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', `checkout_report.${format}`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.$bvToast.toast(`Report exported successfully as ${format.toUpperCase()}`, {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Export failed:', error);
        this.$bvToast.toast('Error exporting report', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    }
  },
}
</script>

<style></style>